import React, { useState, useRef, useEffect } from 'react';
import { graphql } from 'gatsby';
import SEO from '../components/SEO';
import Layout from '../components/Layout';

const Events = (props) => {
  const events = props.data.allEventsJson.edges;
  const [activeFilter, setActiveFilter] = useState('all');
  const [expandedEvents, setExpandedEvents] = useState(new Set());
  const [truncatedEvents, setTruncatedEvents] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const descriptionRefs = useRef({});

  useEffect(() => {
    // Simulate initial load
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Check which descriptions are truncated after render
    // Only check when not loading
    if (isLoading) return;

    const checkTruncation = () => {
      const truncated = new Set();
      let allRefsReady = true;

      events.forEach(({ node }) => {
        const eventId = String(node.id);
        const element = descriptionRefs.current[eventId];

        if (!element) {
          allRefsReady = false;
          return;
        }

        // Check if element is currently expanded - if so, we need to check in non-expanded state
        const isCurrentlyExpanded = expandedEvents.has(eventId);

        // Store original className
        const originalClassName = element.className;

        // Temporarily remove expanded class if present to check original truncation
        if (isCurrentlyExpanded) {
          element.className = originalClassName.replace(/\bexpanded\b/g, '').trim();
          // Force reflow
          void element.offsetHeight;
        }

        // Get computed styles
        const computedStyle = window.getComputedStyle(element);

        // Temporarily remove line-clamp to measure full height
        const originalDisplay = element.style.display;
        const originalWebkitLineClamp = element.style.webkitLineClamp;
        const originalOverflow = element.style.overflow;

        // Remove truncation styles temporarily
        element.style.display = 'block';
        element.style.webkitLineClamp = 'unset';
        element.style.overflow = 'visible';

        // Force reflow
        void element.offsetHeight;

        // Measure full height
        const fullHeight = element.scrollHeight;

        // Restore truncation styles
        element.style.display = originalDisplay;
        element.style.webkitLineClamp = originalWebkitLineClamp;
        element.style.overflow = originalOverflow;

        // Force reflow again
        void element.offsetHeight;

        // Measure truncated height
        const truncatedHeight = element.clientHeight;

        // Restore original className
        element.className = originalClassName;
        // Force reflow
        void element.offsetHeight;

        // If full height is greater than truncated height, text is truncated
        const isTruncated = fullHeight > truncatedHeight + 2; // Small tolerance for rounding

        if (isTruncated) {
          truncated.add(eventId);
        }
      });

      // Only update state if all refs are ready, otherwise retry
      if (allRefsReady) {
        setTruncatedEvents(truncated);
      } else {
        // Retry after a short delay if refs aren't ready
        setTimeout(() => {
          checkTruncation();
        }, 100);
      }
    };

    // Use requestAnimationFrame to ensure styles are applied and refs are set
    // Add a delay to ensure all refs are populated after render
    const timer = setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(checkTruncation);
      });
    }, 200);

    return () => clearTimeout(timer);
  }, [events, isLoading]); // Don't include expandedEvents - truncation should be determined once

  const toggleDescription = (eventId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Ensure consistent string ID
    const id = String(eventId);

    setExpandedEvents((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  return (
    <Layout bodyClass="page-events">
      <SEO title="Events" />

      {isLoading ? (
        <div className="events-loader">
          <div className="loader-container">
            <div className="loader-spinner"></div>
            <p className="loader-text">Loading Events...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Header Section */}
          <div className="events-header">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-12 text-center">
                  <h1 className="events-main-title">Our Events & Programs</h1>
                </div>
              </div>
            </div>
          </div>

          {/* Filter Section */}
          <div className="events-filters">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-12">
                  <div className="filter-buttons">
                    <button
                      className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
                      onClick={() => setActiveFilter('all')}
                    >
                      All Events
                    </button>
                    <button
                      className={`filter-btn ${activeFilter === 'nearest' ? 'active' : ''}`}
                      onClick={() => setActiveFilter('nearest')}
                    >
                      Nearest Events
                    </button>
                    <button
                      className={`filter-btn ${activeFilter === 'latest' ? 'active' : ''}`}
                      onClick={() => setActiveFilter('latest')}
                    >
                      Latest Event
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Events List */}
          <div className="events-list">
            <div className="container">
              <div className="row">
                {events.map(({ node }) => {
                  // Ensure consistent string IDs
                  const eventId = String(node.id);
                  const isExpanded = expandedEvents.has(eventId);
                  const isTruncated = truncatedEvents.has(eventId);
                  return (
                    <div key={eventId} className="col-12 mb-4">
                      <div className="event-card-modern">
                        {/* First Row: Image Left, Title/Description Right */}
                        <div className="row no-gutters event-first-row">
                          <div className="col-12 col-md-5">
                            <div className="event-image-modern">
                              <img
                                src={node.image || '/images/events/placeholder.svg'}
                                alt={node.imageAlt || node.title || 'Event image placeholder'}
                                className="img-fluid event-image-img"
                                onError={(e) => {
                                  // Fallback to team default if placeholder doesn't exist
                                  // if (e.target.src !== '/images/team/default.png') {
                                  e.target.src = '/images/events/placeholder.svg';
                                  // }
                                }}
                              />
                            </div>
                          </div>
                          <div className="col-12 col-md-7">
                            <div className="event-content-modern">
                              <h2 className="event-title-modern">{node.title}</h2>

                              {node.details && (
                                <>
                                  <p
                                    ref={(el) => {
                                      if (el) {
                                        descriptionRefs.current[eventId] = el;
                                      } else {
                                        delete descriptionRefs.current[eventId];
                                      }
                                    }}
                                    className={`event-description ${isExpanded ? 'expanded' : ''}`}
                                    data-event-id={eventId}
                                  >
                                    {node.details}
                                  </p>
                                  {isTruncated && (
                                    <button
                                      type="button"
                                      className="view-more-btn"
                                      onClick={(e) => toggleDescription(eventId, e)}
                                      data-event-id={eventId}
                                    >
                                      {isExpanded ? 'View Less' : 'View More'}
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Second Row: Venue Left, Date Right */}
                        <div className="row no-gutters event-second-row">
                          <div className="col-12">
                            <div className="event-venue-date-row">
                              {node.venue && (
                                <div className="event-info-item venue-item">
                                  <i className="fas fa-map-marker-alt info-icon"></i>
                                  <span className="info-label">Venue:</span>
                                  <span className="info-value">{node.venue}</span>
                                </div>
                              )}
                              {node.date && (
                                <div className="event-info-item date-item">
                                  <i className="fas fa-calendar-alt info-icon"></i>
                                  <span className="info-label">Date:</span>
                                  <span className="info-value">{node.date}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Third Row: Expenditure Full Width */}
                        {node.expenditures && (
                          <div className="row no-gutters event-third-row">
                            <div className="col-12">
                              <div className="event-expenditures-modern">
                                <i className="fas fa-money-bill-alt info-icon"></i>
                                <strong>Expenditure:</strong> {node.expenditures}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </Layout>
  );
};

export const query = graphql`
  query EventsQuery {
    allEventsJson {
      edges {
        node {
          id
          title
          date
          venue
          image
          imageAlt
          details
          expenditures
        }
      }
    }
  }
`;

export default Events;
